import React from 'react';
import {
    Camera,
    Check,
    Eye,
    Flame,
    Gem,
    Handshake,
    Heart,
    Sparkles,
    Truck,
    Zap,
} from 'lucide-react';
import { UICard } from 'ui/card';
import { UIBadge, UIConditionBadge } from 'ui/badge';
import { UIButton } from 'ui/button';
import styles from './ui-product-card.module.scss';

export type UIProductCardVariant = 'vertical' | 'horizontal';
export type UIProductCardBadge = 'deal' | 'hot' | 'new' | 'rare';
export type UIProductCardUrgency = 'hot' | 'rare' | 'fast' | 'new';

export interface UIProductCardProps {
    title: string;
    price: number;
    /**
     * Says what `price` IS, when it is not simply the asking price. Auctions
     * put the current highest bid in this slot, and an unlabelled number there
     * reads as "buy it for this".
     */
    priceLabel?: string;
    originalPrice?: number;
    brand?: string;
    condition?: string;
    image?: { src: string; alt?: string };
    /** Load the image eagerly — set on above-the-fold cards so they can be LCP. */
    priority?: boolean;
    href?: string;
    /** `vertical` is the compact grid card; `horizontal` is the expanded list row. */
    variant?: UIProductCardVariant;
    badges?: UIProductCardBadge[];
    urgency?: { type: UIProductCardUrgency; label: string };
    watching?: number;
    seller?: { name: string; verified?: boolean };
    thumbnailCount?: number;
    /**
     * Free-form layer over the image, for state the design system shouldn't own
     * — sold-out veils, live auction bars, market-comparison badges. Positioned
     * to fill the image area; children place themselves and opt into pointer
     * events as needed.
     */
    imageOverlay?: React.ReactNode;
    /** Shipping is free to every US destination the listing serves. */
    freeShipping?: boolean;
    /** Listing comes from a vetted external partner rather than a GearFocus seller. */
    trustedPartner?: boolean;
    /**
     * Horizontal (list) variant only — domain-owned commerce slots. The list card
     * carries more than the grid card (an image carousel, financing, an explicit
     * shipping price, an Add-to-Cart button, a buyer-protection note); rather than
     * teach this presentational block about carts, Affirm and modals, the domain
     * wrapper passes those in as nodes. All optional; unset = the plain card.
     */
    /** Replaces the single `image` with a custom node (e.g. an image carousel). */
    imageSlot?: React.ReactNode;
    /** Explicit shipping line, shown under the price in the buy column. */
    shippingNote?: React.ReactNode;
    /** Financing line (e.g. "As low as $X/mo with Affirm"). */
    financingSlot?: React.ReactNode;
    /** Primary action; replaces the built-in Buy-now button (e.g. Add to cart). */
    primaryAction?: React.ReactNode;
    /** Trailing note under the action (e.g. the buyer-protection line). */
    footerSlot?: React.ReactNode;
    /**
     * Component that renders the title link. Defaults to a plain `<a>` (keeps the
     * block framework-agnostic); the app passes `next/link` so a card click is a
     * client-side navigation instead of a full page reload.
     */
    /** Open the card's link in a new tab. Carries rel="noopener noreferrer". */
    newTab?: boolean;
    linkComponent?: React.ElementType;
    onClick?: () => void;
    /** Built-in hover heart, for previews and simple consumers. */
    onWishlist?: () => void;
    /**
     * Replaces the built-in heart with your own always-visible control — use this
     * when the button is stateful (favourite counts, auth, toasts), which a
     * design-system element can't own.
     */
    wishlistSlot?: React.ReactNode;
    onBuyNow?: () => void;
    className?: string;
}

// Kept self-contained (no @util import) so this design-system block stays
// unit-testable. Matches formatCurrency's USD output: always two decimals, so a
// dealer-feed price like 239.7 renders "$239.70" (not "$239.7") and a whole
// dollar renders "$875.00" (not "$875").
const usdFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});
const formatPrice = (n: number) => usdFormatter.format(n);

const badgeVariants: Record<UIProductCardBadge, 'deal' | 'primary' | 'success' | 'dark'> = {
    deal: 'deal',
    hot: 'primary',
    new: 'success',
    rare: 'dark',
};

const badgeLabels: Record<UIProductCardBadge, string> = {
    deal: 'Deal',
    hot: 'Hot',
    new: 'New',
    rare: 'Rare',
};

const badgeIcons: Partial<Record<UIProductCardBadge, typeof Flame>> = {
    hot: Flame,
    new: Sparkles,
    rare: Gem,
};

const urgencyIcons: Record<UIProductCardUrgency, typeof Flame> = {
    hot: Flame,
    rare: Gem,
    fast: Zap,
    new: Sparkles,
};

export function UIProductCard({
    title,
    price,
    priceLabel,
    originalPrice,
    brand,
    condition,
    image,
    priority = false,
    href,
    variant = 'vertical',
    badges,
    urgency,
    watching,
    seller,
    thumbnailCount,
    imageOverlay,
    freeShipping = false,
    trustedPartner = false,
    imageSlot,
    shippingNote,
    financingSlot,
    primaryAction,
    footerSlot,
    newTab = false,
    linkComponent: LinkComponent = 'a',
    onClick,
    onWishlist,
    wishlistSlot,
    onBuyNow,
    className = '',
}: UIProductCardProps) {
    // A null/undefined/NaN price used to throw in .toLocaleString() during
    // render and unmount the tree (dealer-feed listings occasionally arrive
    // without a price). Guard it: show a neutral placeholder instead of crashing.
    const [imageFailed, setImageFailed] = React.useState(false);

    const safePrice =
        typeof price === 'number' && Number.isFinite(price) ? price : null;
    const hasDiscount =
        safePrice != null && !!originalPrice && originalPrice > safePrice;
    const discount = hasDiscount
        ? Math.round((1 - safePrice! / originalPrice!) * 100)
        : 0;
    const isHorizontal = variant === 'horizontal';

    const classes = [
        styles.card,
        styles[`variant-${variant}`],
        className,
    ].filter(Boolean).join(' ');

    const priceBlock = (
        <div className={styles.pricing}>
            {priceLabel && (
                <span className={styles.priceLabel}>{priceLabel}</span>
            )}
            <span className={styles.price}>
                {safePrice == null ? '—' : formatPrice(safePrice)}
            </span>
            {hasDiscount && (
                <>
                    <span className={styles.originalPrice}>
                        {formatPrice(originalPrice!)}
                    </span>
                    <UIBadge variant="error" size="sm" pill>
                        -{discount}% OFF
                    </UIBadge>
                </>
            )}
        </div>
    );

    const UrgencyIcon = urgency ? urgencyIcons[urgency.type] : null;

    return (
        <UICard hover className={classes} onClick={onClick}>
            <div className={styles.imageArea}>
                {imageSlot ? (
                    imageSlot
                ) : image && !imageFailed ? (
                    <img
                        src={image.src}
                        alt={image.alt ?? title}
                        className={styles.image}
                        loading={priority ? 'eager' : 'lazy'}
                        fetchPriority={priority ? 'high' : undefined}
                        // Fall back to the same placeholder a listing with no
                        // image gets. Without this a CDN 404 renders the
                        // browser's broken-image glyph. The horizontal variant
                        // is covered because it passes its own imageSlot; the
                        // vertical one lands here, and that is the variant most
                        // listings render in.
                        onError={() => setImageFailed(true)}
                    />
                ) : (
                    <div className={styles.placeholder}>
                        <Camera size={32} />
                    </div>
                )}

                {!!badges?.length && (
                    <div className={styles.badges}>
                        {badges.map((badge) => {
                            const Icon = badgeIcons[badge];
                            return (
                                <UIBadge
                                    key={badge}
                                    variant={badgeVariants[badge]}
                                    size={badge === 'deal' ? 'md' : 'sm'}
                                    pill={badge !== 'deal'}
                                >
                                    {Icon && <Icon size={11} />}
                                    {badgeLabels[badge]}
                                </UIBadge>
                            );
                        })}
                    </div>
                )}

                {wishlistSlot ? (
                    // Stays above the stretched title link so clicks reach the
                    // control, and always visible — hover-only would be
                    // unreachable on touch.
                    <div
                        className={styles.wishlistSlot}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {wishlistSlot}
                    </div>
                ) : (
                    onWishlist && (
                        <button
                            type="button"
                            className={styles.wishlist}
                            aria-label="Add to wishlist"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onWishlist();
                            }}
                        >
                            <Heart size={14} />
                        </button>
                    )
                )}

                {!!thumbnailCount && (
                    <div className={styles.thumbnailCount}>
                        <Camera size={12} />
                        <span>{thumbnailCount}</span>
                    </div>
                )}

                {imageOverlay && (
                    <div className={styles.imageOverlay}>{imageOverlay}</div>
                )}
            </div>

            <div className={styles.body}>
                <div className={styles.topRow}>
                    {brand && <span className={styles.brand}>{brand}</span>}
                    {condition && (
                        <UIConditionBadge label={condition} variant="dot" size="sm" />
                    )}
                </div>

                {/* The title link stretches over the whole card via ::after, so the
                    entire surface is clickable from a single anchor. The wishlist and
                    buy controls sit above it and stay independently clickable. */}
                {href ? (
                    <LinkComponent
                        href={href}
                        className={styles.titleLink}
                        // noopener/noreferrer with _blank: without it the opened
                        // tab gets a window.opener handle back to this page.
                        {...(newTab
                            ? { target: '_blank', rel: 'noopener noreferrer' }
                            : {})}
                    >
                        <h4 className={styles.title}>{title}</h4>
                    </LinkComponent>
                ) : (
                    <h4 className={styles.title}>{title}</h4>
                )}

                {urgency && UrgencyIcon && (
                    <UIBadge variant="primary" size="sm" pill>
                        <UrgencyIcon size={11} />
                        {urgency.label}
                    </UIBadge>
                )}

                {(seller || !!watching) && (
                    <div className={styles.metaRow}>
                        {seller && (
                            <span className={styles.seller}>
                                {seller.name}
                                {seller.verified && (
                                    <Check
                                        size={12}
                                        className={styles.verified}
                                        aria-label="Verified seller"
                                    />
                                )}
                            </span>
                        )}
                        {!!watching && (
                            <span className={styles.watching}>
                                <Eye size={12} />
                                {watching} watching
                            </span>
                        )}
                    </div>
                )}

                {!isHorizontal && priceBlock}

                {(freeShipping || trustedPartner) && (
                    <div className={styles.notes}>
                        {freeShipping && (
                            <span className={`${styles.note} ${styles.noteShipping}`}>
                                <Truck size={12} aria-hidden="true" />
                                Free shipping
                            </span>
                        )}
                        {trustedPartner && (
                            <span className={`${styles.note} ${styles.notePartner}`}>
                                <Handshake size={12} aria-hidden="true" />
                                Trusted partner
                            </span>
                        )}
                    </div>
                )}

                {/* Vertical variant's footer slot (e.g. GB web#1005's GearMaster
                    back-link). Horizontal renders footerSlot in buyColumn below —
                    mutually exclusive on isHorizontal, so it's never rendered twice.
                    Wrapped with the same z-index lift buyColumn already has: the
                    title link stretches over the whole card, and anything after it
                    here would otherwise paint underneath that overlay and be
                    unclickable despite rendering correctly. */}
                {!isHorizontal && footerSlot && (
                    <div className={styles.footerSlotVertical}>{footerSlot}</div>
                )}
            </div>

            {isHorizontal && (
                <div className={styles.buyColumn}>
                    {priceBlock}
                    {shippingNote}
                    {financingSlot}
                    {primaryAction ??
                        (onBuyNow && (
                            <UIButton
                                variant="primary"
                                size="md"
                                block
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onBuyNow();
                                }}
                            >
                                Buy now
                            </UIButton>
                        ))}
                    {footerSlot}
                </div>
            )}
        </UICard>
    );
}

export default UIProductCard;
